// AddressSelection.jsx
import React, { useState } from "react";
import CheckoutDL from "../../../components/deliveryLoctions/CheckoutDL";

export default function AddressSelection({
  locations,
  selectedLocation,
  setSelectedLocation,
}) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Pending selection while dialog is open — persists across close/reopen
  // so "Change delivery location" remembers the last pick, same as mosque flow.
  const [pendingLocation, setPendingLocation] = useState(selectedLocation);

  const openDialog = () => {
    // Keep dialog's radio list in sync with whatever is currently confirmed,
    // in case selectedLocation changed elsewhere (e.g. default loaded later).
    setPendingLocation((prev) => prev || selectedLocation);
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    // Deliberately do NOT reset pendingLocation — keeps last pick remembered.
    setIsDialogOpen(false);
  };

  const handleConfirm = () => {
    if (!pendingLocation) return;
    setSelectedLocation(pendingLocation);
    setIsDialogOpen(false);
  };

  return (
    <div className="mx-auto">
      <div className="text-left mb-2">
        <span className="text-base text-cool-gray-600 font-poppins font-normal">
          Delivery Address
        </span>
      </div>

      {/* ===================================== */}
      {/* TRIGGER: summary card OR "select" card */}
      {/* ===================================== */}
      {selectedLocation ? (
        <div className="flex items-center justify-between border border-secondary rounded-md shadow-sm p-4 bg-navbarBackground">
          <div className="flex-1">
            <div className="flex items-center gap-x-3 mb-1">
              {selectedLocation.isDefault && (
                <p className="rounded-full bg-green-100 px-1.5 py-0.5 text-C11 font-semibold whitespace-nowrap text-green-700 ring-1 ring-green-600/20 ring-inset">
                  Default
                </p>
              )}
              <p className="text-sm font-semibold text-gray-900 uppercase">
                {selectedLocation.locationLabel}
              </p>
            </div>
            <p className="text-sm text-gray-800">
              {selectedLocation.recipientName} ({selectedLocation.phoneNumber})
            </p>
            <p className="text-sm text-gray-600">
              {selectedLocation.areaName}, {selectedLocation.cityName}
            </p>
          </div>

          <button
            type="button"
            onClick={openDialog}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-secondary text-textWhite hover:text-textBlack border border-primaryLight rounded-md transition shrink-0 ml-4"
          >
            {/* ICON SLOT: e.g. edit/pencil icon */}
            <span className="w-4 h-4 inline-block" />
            Change delivery location
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={openDialog}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-md text-sm font-medium text-gray-600 hover:border-primary hover:text-primary transition"
        >
          {/* ICON SLOT: e.g. location/plus icon */}
          <span className="w-5 h-5 inline-block" />
          Select delivery location
        </button>
      )}

      {/* ===================================== */}
      {/* DIALOG: address picker */}
      {/* ===================================== */}
      {isDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-primaryVeryLight rounded-lg shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center mb-4">
              <div className="bg-blue-100 rounded-full p-3 mr-4">
                {/* ICON SLOT: e.g. location pin icon */}
                <span className="w-6 h-6 inline-block" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">
                Select Delivery Location
              </h3>
            </div>

            <div className="mb-4">
              {Array.isArray(locations) && locations.length > 0 ? (
                <CheckoutDL
                  locations={locations}
                  onSelect={setPendingLocation}
                  selectedLocation={pendingLocation}
                />
              ) : (
                <p className="text-sm text-gray-600">
                  No saved addresses found. Please add one from your Address
                  Book.
                </p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={closeDialog}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 hover:bg-darkVeryLight rounded-md text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 transition"
              >
                {/* ICON SLOT: e.g. x icon */}
                <span className="w-4 h-4 inline-block" />
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={!pendingLocation}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md border border-transparent bg-primary hover:bg-primaryHover text-base font-medium hover:text-textBlack text-textWhite shadow-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {/* ICON SLOT: e.g. check icon */}
                <span className="w-4 h-4 inline-block" />
                Confirm delivery location
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
